import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bars3Icon,
  ArrowRightOnRectangleIcon,
  KeyIcon,
  UserCircleIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  BookOpenIcon,
  SparklesIcon,
  LanguageIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import useCompanyProfile from '../../hooks/useCompanyProfile';

/*
 * AppBar (top bar) shows the COMPANY NAME on every page — LAT-05.
 * The page name belongs in a <PageHeader> H1 in the body, not here.
 * Old getPageTitle() was removed; if you need a fallback for unauth'd /
 * pre-load states, the company-name slot renders the user's email.
 */

function HelpMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 min-w-[44px] min-h-[44px] flex items-center justify-center"
        style={{ borderRadius: 'var(--radius-md)' }}
        aria-label="Help"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <QuestionMarkCircleIcon className="h-5 w-5" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-56 bg-white ring-1 ring-black/5 py-1 z-50"
          style={{ borderRadius: 'var(--radius-md)' }}
          role="menu"
          aria-label="Help menu"
        >
          <button
            onClick={() => { setOpen(false); navigate('/help'); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            role="menuitem"
          >
            <BookOpenIcon className="h-4 w-4 text-gray-400" />
            Help Center
          </button>

          <button
            onClick={() => { setOpen(false); navigate('/help/feedback'); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            role="menuitem"
          >
            <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-400" />
            Submit Feedback
          </button>

          <button
            onClick={() => { setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            role="menuitem"
          >
            <SparklesIcon className="h-4 w-4 text-gray-400" />
            What's New
          </button>

          <div className="border-t border-gray-100 mt-1 pt-1 px-4 py-2">
            <p className="text-xs text-gray-400">
              Keyboard shortcuts: press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">?</kbd>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Header({ onMenuClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { companyName } = useCompanyProfile();
  // AppBar shows the COMPANY NAME, not the page name (LAT-05)
  const displayName = companyName || 'BIDIT';
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="flex h-14 items-center justify-between px-4 md:px-6 flex-shrink-0" style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
      {/* Left: hamburger (mobile) + page title */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="p-2 text-gray-500 hover:bg-gray-100 lg:hidden min-w-[44px] min-h-[44px] flex items-center justify-center"
          style={{ borderRadius: 'var(--radius-md)' }}
          aria-label="Open navigation menu"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>

        {/*
         * Company name slot — NOT the page title. This element is intentionally
         * a <span> (not <h1>) because the page-level H1 lives in <PageHeader>
         * inside the body. Keeping a single H1 per page is an a11y requirement.
         */}
        <span
          className="text-lg font-semibold truncate"
          style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-display)' }}
          aria-label="Company"
        >
          {displayName}
        </span>
      </div>

      {/* Right: help + notifications + user menu */}
      <div className="flex items-center gap-1">
        {/* Help menu */}
        <HelpMenu />

        {/* Notifications */}
        {/* G1: Bell removed — no notification bell anywhere */}

        {/* User/Avatar menu dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2 pl-2 border-l border-[color:var(--color-border)] hover:bg-gray-50 py-1 pr-2 transition-colors ml-1"
            aria-label="User menu"
            aria-expanded={menuOpen}
            aria-haspopup="true"
          >
            <div className="h-8 w-8 flex items-center justify-center" style={{ backgroundColor: 'var(--red-100)', borderRadius: '3px' }}>
              <span className="text-xs font-medium" style={{ color: 'var(--red-700)' }}>
                {user?.firstName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
              </span>
            </div>
            <span className="hidden md:block text-sm text-gray-600 max-w-[150px] truncate">
              {user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.email || 'User'}
            </span>
            <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 hidden md:block" />
          </button>

          {/* Avatar dropdown menu */}
          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-1 w-64 bg-white ring-1 ring-black/5 py-1 z-50"
              style={{ borderRadius: 'var(--radius-md)' }}
              role="menu"
              aria-label="User menu"
            >
              {/* User info */}
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>

              {/* Menu items */}
              <button
                onClick={() => { setMenuOpen(false); navigate('/profile'); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                role="menuitem"
              >
                <UserCircleIcon className="h-4 w-4 text-gray-400" />
                My Profile
              </button>

              <button
                onClick={() => { setMenuOpen(false); navigate('/change-password'); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                role="menuitem"
              >
                <KeyIcon className="h-4 w-4 text-gray-400" />
                Change Password
              </button>

              {/* §3 (2026-08-08): language picker stub. English is the only
                  working option; Español/Português stay visible but disabled
                  to signal roadmap. */}
              <div className="px-4 py-2">
                <div className="flex items-center gap-3 mb-1">
                  <LanguageIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-700">Language</span>
                </div>
                <div className="ml-7 space-y-1">
                  <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    ✓ English
                  </div>
                  <div className="text-sm" style={{ color: 'var(--color-text-disabled)', cursor: 'not-allowed' }}>
                    Español <span className="text-xs">(coming soon)</span>
                  </div>
                  <div className="text-sm" style={{ color: 'var(--color-text-disabled)', cursor: 'not-allowed' }}>
                    Português <span className="text-xs">(coming soon)</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 mt-1 pt-1">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  role="menuitem"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                  Sign out
                </button>
              </div>

              {/* Version info */}
              <div className="border-t border-gray-100 mt-1 px-4 py-2">
                <p className="text-xs text-gray-400">
                  BIDIT v4.0 • Build {process.env.REACT_APP_BUILD_ID || 'dev'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
